/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { EditableComponent } from '@adobe/aem-react-editable-components';
import { AvailabilityCalendarProps, AlertMessageBodyProps, AEMAlertBodyProps } from './AvailabilityCalendar.types';
import { useConditionalPageContextADF } from '../../hooks';
import { CalendarWrapper } from '../../molecules/CalendarWrapper';
import { StyledAvailabilityCalendarDiv } from './AvailabilityCalendar.styles';
import { TabComponent, Text, Types } from '@marriott/mi-ui-library';
import { eventUtil } from '@marriott/mi-ui-library-shop';
import { phoenixADFShopSearchProductsByProperty } from './queries';
import { useCheckBreakpoint } from '../../hooks';
import { useApolloClient } from '@apollo/client';
import moment from 'moment';
import { Messages } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import {
  AVG_TAB,
  DATE_FORMAT_YEAR_MONTH_DAY,
  NUMBER_OF_DAYS_FOR_PREVIOUS_MONTH,
  TOTAL_NUMBER_OF_DAYS_FOR_DATA,
  TOTAL_TAB,
  TOO_MANY_ROOMS_FOR_FLEXIBLE_ERROR,
  GROUP_CODE_FEW_CHARS_ERROR,
  CORP_CODE_CHARS_LENGTH_ERROR,
  GROUP_CLUSTER_CODE,
  CORP_CLUSTER_CODE,
  FUTURE_DATE_ERRROR,
  CORP_CODE_CHARS_INVALID_ERROR,
  ADULT_ONLY_CHILD_COUNT_MORE_THAN_ZERO_ERROR,
  CURRENCY_CONVERSION_FAILED,
} from '../../constants/Calendar.constant';
import {
  updateDataLayerOnInteractions,
  updateDataLayerObj,
  convertStateValuestoFormValues,
  isConcurFlowActive,
  hasConcurUserOverride,
} from '../../modules/utils';
import { validateAvailabilityRequest } from '../../lib/organisms/SearchForm/utils/Helpers';
import { SkeletonLoader } from '../../molecules/SkeletonLoader';
import { REWARDS_REDEMPTION_CLUSTER_CODES } from '../../constants/ADFCalendar.constant';
import { LATIN_NAME_REGEX } from '../../constants/lib/constants';
import {
  fetchCurrencyToLocale,
  formatDateStringBasedOnLocale,
  getClusterCodeFromRateRequestType,
  getConcurAwareRateRequestType,
} from '../../utils/src/helper';
import { phoenixShopHQVRateOnly } from '../HotelGalleryTitle/HQVQueries';
import mockResponse from './__mock__/AvailabilityCalendarAuthorMode.model.json';
import { useSearchFormStore } from '../../modules/store/searchFormStore';
import { formAvailablitySearchRequestParams } from '../../modules/utils';
import { useStaticDataContext } from '../../modules/context';
import { useClientEnvVarsStore, usePersistentGlobalStore, useUserDetailsGlobalStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';
import { useGetUserInfo } from '../../hooks/lib/useGetUserInfo';
import { lockBodyScroll, unlockBodyScroll } from '../../utils';
import { hqvLog } from '../../constants';
import { useConditionalHQVContextADF } from '../../hooks/lib/useConditionalHQVContext';
declare global {
  interface Window {
    // Setting type to any to stay in sync with book team sessionData type declaration
    sessionData: any;
  }
}

export const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = props => {
  const checkLocaleAndConvertNumberInDate = (dateString: string) => {
    return formatDateStringBasedOnLocale(dateString);
  };
  const { model } = props;
  const [showCalendar, setShowCalendar] = useState(false);
  const jsonContent = { ...model };
  const [dataLoading, setDataLoading] = useState(true);
  const ersManFees = useRef<any>();
  const isDesktop = useCheckBreakpoint('viewportL');
  const { searchCTAEndpointURL } = useStaticDataContext();
  const updateGlobalData = usePersistentGlobalStore((state: any) => state.updateGlobalData);
  const pageContext = useConditionalPageContextADF();

  // HQV Modal state for body scroll control.
  // Use the ADF-scoped flag-aware hook so this reader honors EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND
  // and stays in sync with SearchFormComponent (ADF modal owner). When the flag is OFF the value
  // comes from React Context (where SearchFormComponent's writes land); when ON it comes from
  // Zustand. This avoids the previous split-brain read that always used Zustand.
  const { hqvState } = useConditionalHQVContextADF();
  const modalOpenState = hqvState.modalOpenState;

  const {
    sessionData,
    setSessionData,
    currentLocale,
    resolvedUrl,
    headersData,
    query,
    datalayerParams,
    datalayerProperties,
    interceptorResponse,
    preHeader,
    NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL,
    isBulgariDomain,
    pageState,
    isAuthorMode,
    requestId,
  } = pageContext;

  const skipQuery = useMemo(() => {
    return !!pageState?.authorMode || !!isAuthorMode;
  }, [pageState, isAuthorMode]);

  const [updatedSessionData, setUpdatedSessionData] = useState(sessionData);

  const sessionID = sessionData?.sessionToken
    ? sessionData?.sessionToken
    : sessionData?.data?.sessionToken
    ? sessionData?.data?.sessionToken
    : sessionData?.cacheData?.key
    ? sessionData?.cacheData?.key
    : sessionData?.cacheData?.data?.sessionToken;

  const { AriesSearch, AriesCommon, AriesReservation, consumerID } =
    sessionData?.data ?? sessionData?.cacheData?.data ?? {};
  const { availabilityRequestVO, showFullPrice } = AriesSearch?.searchCriteria || {};
  const { selectedCurrency, propertyId } = AriesReservation || {};
  const search_fusionsearchid = AriesCommon?.search_fusionsearchid;
  const { envVarsObject } = useClientEnvVarsStore();

  // Diagnostic: what HQV routing is this ADF page using? Mirrors the log emitted by
  // SearchFormComponent so both ADF entry points confirm the resolved store consistently.
  // Declared here (after envVarsObject) rather than next to the state read above because the
  // client env store hook is initialized further down; moving it up would require also hoisting
  // useClientEnvVarsStore().
  useEffect(() => {
    const featureFlags = envVarsObject?.['featureFlags'] as unknown as Record<string, FLAG_TYPE> | undefined;
    // Skip until client env store has hydrated to avoid reporting flag=false pre-hydration.
    if (!featureFlags || Object.keys(featureFlags).length === 0) return;
    const adfZustandOn = featureFlags[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND] ?? false;
    const store = adfZustandOn ? 'Zustand' : 'Context';

    hqvLog.log('[AvailabilityCalendar] HQV routing:', {
      page: 'ADF',
      EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND: adfZustandOn,
      effectiveMode: 'Legacy',
      store,
    });
  }, [envVarsObject]);

  const {
    checkInDate,
    clusterCode,
    corporateCode,
    numRooms,
    numGuestsPerRoom,
    rewardsRedemption,
    lengthOfStay,
    flexibleDate,
    groupCode,
  } = availabilityRequestVO || {};
  const [adfData, setAdfData] = useState<any>();
  const [formData, setFormData] = useState<any>();
  const alertBody: AEMAlertBodyProps[] = jsonContent?.['adfAlertBody'] as any;
  const setIsInvalidCorpCode = useSearchFormStore((state: any) => state?.setIsInvalidCorpCode);

  const [selectedDays, setSelectedDays] = useState(lengthOfStay > 0 && lengthOfStay <= 9 ? lengthOfStay : 1);
  const [selectedTab, setSelectedTab] = useState(lengthOfStay > 1 ? AVG_TAB : TOTAL_TAB);
  const [isPointsSelected, setIsPointsSelected] = useState(rewardsRedemption);
  const [selectedCluster, setSelectedCluster] = useState(clusterCode);
  const [selectedCorporateCode, setSelectedCorporateCode] = useState(
    clusterCode?.toLowerCase() === 'group' ? groupCode : corporateCode
  );
  const [selectedNumOfGuests, setSelectedNumOfGuests] = useState(numGuestsPerRoom);
  const [selectedRoomCount, setSelectedRoomCount] = useState(numRooms);
  const inventoryDates = AriesCommon?.singleDateLimit;
  const [dateRange, setDateRange] = useState<Array<string>>([]);
  const [defaultMonthDate, setdefaultMonthDate] = useState(
    checkLocaleAndConvertNumberInDate(
      moment(checkInDate).utc().format(DATE_FORMAT_YEAR_MONTH_DAY) ?? moment().format(DATE_FORMAT_YEAR_MONTH_DAY)
    )
  );
  const [isRedemption, setIsRedemption] = useState(false);

  const { size, tags } = Types;
  const [alertMessage, setAlertMessage] = useState<AlertMessageBodyProps>({});
  const [updateAdfSearchCounter, setUpdateAdfSearchCounter] = useState<number>(0);
  const isViewportM = useCheckBreakpoint('viewportM');

  const [isAdultsOnly, setIsAdultsOnly] = useState<boolean>(false);
  const [childrenCount, setChildrenCount] = useState<number>(0);
  const [fromCurrency, setFromCurrency] = useState<string>('');

  const currentCurrency =
    (selectedCurrency && selectedCurrency.toLowerCase() !== 'default' ? selectedCurrency : fromCurrency) ??
    AriesReservation?.propertyCurrency ??
    'USD';

  const includeTaxesAndFees = showFullPrice === 'true' ? true : false;
  const [isConcurCodeFetched, setIsConcurCodeFetched] = useState<boolean>(false);
  const queryCommonContext = {
    /**
     * define common params for all the query
     */
    context: {
      headers: {
        'x-request-id': requestId,
        'accept-language': currentLocale?.replace('_', '-'),
      },
    },
  };
  let tabList = [
    {
      tabTitle: model?.['avgNightlyCostTabText'],
      tabSubtitle:
        model?.['avgNightlyCostPlaceholderText'] +
        ` (${fetchCurrencyToLocale(currentLocale, jsonContent?.['currencyLabel'], currentCurrency)})`,
      tabValue: AVG_TAB,
      isActive: false,
    },
    {
      tabTitle: model?.['totalRoomCostTabText'],
      tabSubtitle:
        model?.['totalRoomCostPlaceholderText'] +
        ` (${fetchCurrencyToLocale(currentLocale, jsonContent?.['currencyLabel'], currentCurrency)})`,
      tabValue: TOTAL_TAB,
      isActive: false,
    },
  ];

  if (selectedDays < 2 || model?.['disableAvgNightlyCostSection']) {
    tabList = tabList.filter(item => item.tabValue !== AVG_TAB);
  }
  if (model?.['disableTotalRoomCostSection']) {
    tabList = tabList.filter(item => item.tabValue !== TOTAL_TAB);
  }

  useEffect(() => {
    if (selectedDays < 2) {
      setSelectedTab(TOTAL_TAB);
    } else {
      setSelectedTab(AVG_TAB);
    }
    setShowCalendar(true);
    // Get the query parameters from the URL
    const searchParams = new URLSearchParams(window.location.search);
    const costTabValue = searchParams.get('costTab');
    if (costTabValue) {
      if (costTabValue === 'average' && selectedDays > 1) {
        setSelectedTab(AVG_TAB);
      } else {
        setSelectedTab(TOTAL_TAB);
      }
    } else {
      if (lengthOfStay > 1) {
        setSelectedTab(AVG_TAB);
      } else {
        setSelectedTab(TOTAL_TAB);
      }
    }
  }, []);

  // Body overflow control for HQV modal
  // When HQV modal is open, prevent background scrolling
  useEffect(() => {
    if (modalOpenState) {
      document.body.style.overflow = 'hidden';
      if (!isDesktop) {
        lockBodyScroll();
      }
    } else {
      document.body.style.overflow = 'auto';
      if (!isDesktop) {
        unlockBodyScroll();
      }
    }
  }, [modalOpenState, isDesktop]);

  // Cleanup on unmount - restore scroll
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
      if (!isDesktop) {
        unlockBodyScroll();
      }
    };
  }, []);

  useEffect(() => {
    if (adfData && adfData?.calendarSearchByProperty) {
      const firstNode = adfData?.calendarSearchByProperty?.edges[0];
      if (firstNode?.node?.rateModes?.lowestAverageRate?.amount?.currency) {
        setFromCurrency(firstNode.node.rateModes.lowestAverageRate.amount?.currency);
      }
    }
  }, [adfData]);

  const SearchCounterValue = () => {
    /**
     * To get and set Counter value in sessionStorage
     * and later updating hash in the url
     * */
    if (typeof window !== 'undefined') {
      if (sessionStorage.getItem('updateAdfSearchCounter')) {
        const maxValue = parseInt(sessionStorage.getItem('updateAdfSearchCounter') as string);
        /**
         * set the previous counter value with +1 on update search
         */
        sessionStorage.setItem('updateAdfSearchCounter', (maxValue + 1).toString());
        setUpdateAdfSearchCounter(maxValue + 1);
      } else {
        sessionStorage.setItem('updateAdfSearchCounter', `${updateAdfSearchCounter}`);
      }
      const searchCounterValue = sessionStorage.getItem('updateAdfSearchCounter');
      // Append the counter value to the window
      if (window && window.dataLayer) {
        window.dataLayer['updates_search_call_counter'] = searchCounterValue;
      }
      // Update the window.location.hash with the update search counter
      window.history.replaceState({}, headersData?.referer, `#/${searchCounterValue}/`);
    }
  };

  const userDetailsState = useUserDetailsGlobalStore(state => state);

  // Calculate session token for useGetUserInfo hook
  const featureFlags = envVarsObject?.['featureFlags'] as unknown as Record<string, any> | undefined;
  const shouldCallGetUserInfo =
    sessionData?.authenticated &&
    featureFlags &&
    isConcurFlowActive(featureFlags, sessionData?.data || sessionData?.cacheData?.data) &&
    !userDetailsState?.isUserDetailsSaved;

  const sessionTokenForUserInfo = shouldCallGetUserInfo
    ? sessionData?.data?.sessionToken || sessionData?.cacheData?.data?.sessionToken
    : undefined;

  const { hasSettled: userInfoSettled } = useGetUserInfo({
    sessionToken: sessionTokenForUserInfo,
    requestId,
    currentLocale,
  });
  useEffect(() => {
    const featureFlags = envVarsObject?.['featureFlags'] as unknown as Record<string, any> | undefined;

    if (!sessionData?.authenticated) {
      setIsConcurCodeFetched(true);
      return;
    }

    if (!featureFlags) {
      return;
    }

    if (isConcurFlowActive(featureFlags, sessionData?.data || sessionData?.cacheData?.data)) {
      if (userDetailsState?.isUserDetailsSaved) {
        // Check if user has manually overridden Concur in this session
        if (hasConcurUserOverride()) {
          setIsConcurCodeFetched(true);
          return;
        }
        setIsConcurCodeFetched(true);
      } else if (userInfoSettled) {
        // getUserInfo failed (e.g. 500) — proceed with default rates
        setIsConcurCodeFetched(true);
      }
    } else {
      setIsConcurCodeFetched(true);
    }
  }, [sessionData?.authenticated, userDetailsState?.isUserDetailsSaved, envVarsObject, userInfoSettled]);
  useEffect(() => {
    const lastMonthDates = moment(checkInDate)
      ?.utc()
      .startOf('M')
      .subtract(getUTCNoOfPrevMonthDays(moment(checkInDate)?.utc().startOf('M')), 'days'); // get date 6 days older to current month date
    const isPastDate = lastMonthDates?.diff(moment()) < 0 ? true : false; // check if above date is older than today
    const startDate = isPastDate ? moment() : lastMonthDates; // if 'lastMonthDates' is past date then fromDate is today , else fromDate is 'lastMonthDates'

    const endDate = startDate
      .clone()
      .add(
        isPastDate && lastMonthDates.format('MMMM') === moment().format('MMMM')
          ? Number(
              getUTCNoOfPrevMonthDays(moment(checkInDate)?.utc().startOf('M')) >= 5 ? TOTAL_NUMBER_OF_DAYS_FOR_DATA : 34
            ) + Number(lastMonthDates?.diff(moment(), 'days'))
          : getUTCNoOfPrevMonthDays(moment(checkInDate)?.utc().startOf('M')) >= 5
          ? TOTAL_NUMBER_OF_DAYS_FOR_DATA
          : 34,
        'days'
      )
      .format(DATE_FORMAT_YEAR_MONTH_DAY);
    const startDateFormatted = checkLocaleAndConvertNumberInDate(startDate.format(DATE_FORMAT_YEAR_MONTH_DAY));
    setdefaultMonthDate(
      checkLocaleAndConvertNumberInDate(
        moment(checkInDate).utc().diff(moment().startOf('M')) < 0
          ? moment().format(DATE_FORMAT_YEAR_MONTH_DAY)
          : moment(checkInDate).utc().format(DATE_FORMAT_YEAR_MONTH_DAY)
      )
    ); // change default month to show on ADF calendar based on start date
    const rateRequestType = getConcurAwareRateRequestType(
      rewardsRedemption,
      clusterCode,
      corporateCode,
      groupCode,
      userDetailsState,
      getRateRequestType
    );
    const requestObj = {
      //send property id/marsha code to retrieve basic information
      id: [propertyId],
      search: {
        propertyId: propertyId,
        options: {
          startDate: checkLocaleAndConvertNumberInDate(startDateFormatted),
          numberOfRooms: Number(numRooms),
          rateRequestTypes: rateRequestType,
          endDate:
            moment(endDate).diff(moment(inventoryDates), 'd') > 0
              ? checkLocaleAndConvertNumberInDate(
                  moment(inventoryDates).subtract(1, 'd').format(DATE_FORMAT_YEAR_MONTH_DAY)
                )
              : checkLocaleAndConvertNumberInDate(endDate),
          numberInParty: numGuestsPerRoom,
          numberOfDays: Math.trunc(lengthOfStay) > 9 ? 1 : Math.trunc(lengthOfStay), //In case of flexible search maximum allowed nights is 9 so  length of stay is reset to 1 incase of morethan 9 nights in flexible and  need this variable to calculate total price
          customerId: consumerID,
        },
      },
    };
    setDateRange([checkLocaleAndConvertNumberInDate(startDateFormatted), checkLocaleAndConvertNumberInDate(endDate)]);
    setDataLoading(true);

    /**
     * To get and set Counter value in sessionStorage
     * and later updating hash in the url on the page-reload
     * */
    SearchCounterValue();
    if (skipQuery) {
      // Author mode: use mock response
      setAdfData(mockResponse.data.search);
      setDataLoading(false);
    } else if (isConcurCodeFetched) {
      getADFSearchDataForDTT(requestObj);
    } else {
      // Keep skeleton loaders visible while waiting for logged-in user prerequisites (e.g. Concur/user info)
      setDataLoading(true);
    }
    const updatedConcurClusterCode = getClusterCodeFromRateRequestType(rateRequestType, clusterCode);
    updateDataLayerOnInteractions(
      resolvedUrl,
      headersData,
      query,
      datalayerParams,
      currentLocale,
      sessionID,
      propertyId,
      search_fusionsearchid,
      isBulgariDomain,
      undefined,
      undefined,
      updatedConcurClusterCode
    );
    eventUtil.dispatch('loadSearchForm', true);
  }, [isConcurCodeFetched]);

  // Use effect to handle global dataLayer updates
  useEffect(() => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      const searchTypeValue = AriesSearch?.searchCriteria?.searchType;
      window.dataLayer['searchType'] = searchTypeValue;
    }
  }, [AriesSearch?.searchCriteria?.searchType]);

  /**
   * Function to manipulate the aem  content to be display no of nights based on user selection
   */
  const replaceDays = () => {
    for (let i = 0; i < tabList?.length; i++) {
      tabList[i].tabSubtitle = tabList[i]?.tabSubtitle
        ?.replace(/[0-9]/g, `${selectedDays}`)
        ?.replace('{X}', `${selectedDays}`);

      if (selectedDays < 2) {
        tabList[i].tabSubtitle = tabList[i]?.tabSubtitle?.replace('nights', 'night');
      }
    }
  };
  replaceDays();

  //below code is to call UXL on page load once inventoryDates are available

  const client = useApolloClient();

  const getADFSearchDataForDTT = async (variables: any, sessionDataObj?: any) => {
    try {
      const response: any = await client.query({
        query: phoenixADFShopSearchProductsByProperty,
        variables,
        ...queryCommonContext,
        fetchPolicy: 'network-only',
      });
      loadAfterSearchData(response?.data, sessionDataObj, variables);
      //dispatch event to capture in CalendarWrapper
      eventUtil.dispatch('sendMandatoryFeeDescription', response?.data?.propertiesByIds?.[0]?.basicInformation);
    } catch {
      setDataLoading(false);
    }
  };

  const findFirstAvailableDate = (edges: any) => {
    const firstAvailableDateNode = edges.find((node: any) => {
      const dttVersionNode: any = node;
      return (
        dttVersionNode?.node?.rateModes?.lowestAverageRate?.amount?.amount ||
        dttVersionNode?.node?.rateModes?.pointsPerQuantity?.points
      );
    });
    return firstAvailableDateNode?.node?.startDate;
  };

  // function to call UXL once we get response from UXL
  const loadAfterSearchData = (data: any, sessionDataObj?: any, variables?: any) => {
    const adfDataFromResp = data.search;
    setAdfData(adfDataFromResp);
    const edges = adfDataFromResp?.calendarSearchByProperty?.edges;
    const firstAvailableDate = edges && findFirstAvailableDate(edges as any);
    const sessionData = sessionDataObj ?? updatedSessionData;
    displaySessionBasedAlerts(sessionData);
    const isAdultsOnly = data?.propertiesByIds[0]?.basicInformation?.isAdultsOnly;
    setIsAdultsOnly(isAdultsOnly);
    // Update the isAdultsOnly query param value
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('isAdultsOnly', isAdultsOnly);
    window.history.replaceState({}, headersData?.referer, decodeURIComponent(`?${searchParams.toString()}`));
    const searchCounterValue = sessionStorage.getItem('updateAdfSearchCounter');
    // Update the window.location.hash with the current search counter
    window.history.replaceState({}, headersData?.referer, `#/${searchCounterValue}/`);
    if (
      firstAvailableDate &&
      currentLocale === 'en-US' //Mandatory fee call should happen if any of the dates has rate(if all dates are not disabled) for all properties
    ) {
      if (AriesReservation?.description) {
        fetchMandatoryFees({
          variables: {
            search: {
              ids: [propertyId],
              options: {
                startDate: checkLocaleAndConvertNumberInDate(
                  moment(firstAvailableDate).format(DATE_FORMAT_YEAR_MONTH_DAY)
                ),
                endDate: checkLocaleAndConvertNumberInDate(
                  moment(firstAvailableDate).add(1, 'd').format(DATE_FORMAT_YEAR_MONTH_DAY)
                ),
                quantity: 1,
                numberInParty: 1,
                rateRequestTypes: variables?.search?.options?.rateRequestTypes,
                includeMandatoryFees: true,
                childAges: [],
                includeUnavailableProperties: true,
                customerId: consumerID,
              },
            },
          },
          context: {
            headers: {
              // Any header, including x-request-id, can be passed in args with query.
              // If you don't pass it in the authLink will generate a random ID.
              'x-request-id': requestId,
              'accept-language': currentLocale,
            },
          },
        });
      } else {
        setDataLoading(false);
      }
    } else {
      setDataLoading(false);
    }
  };

  //function to determine the request type to send to UXL based on search form data / session data
  const getRateRequestType = (usePoints: boolean, clusterCode: string, corporateCode: string) => {
    setIsRedemption(false);
    let rateRequestTypes;
    if (usePoints) {
      rateRequestTypes = [
        {
          type: 'REDEMPTION',
        },
      ];
    } else {
      rateRequestTypes = [
        {
          value: '',
          type: 'STANDARD',
        },
      ];
      if (clusterCode.toLowerCase() === 'aaa') {
        rateRequestTypes = [
          {
            value: '',
            type: 'AAA',
          },
        ];
      } else if (clusterCode.toLowerCase() === 'gov') {
        rateRequestTypes = [
          {
            value: '',
            type: 'GOV',
          },
        ];
      } else if (clusterCode.toLowerCase() === 's9r') {
        rateRequestTypes = [
          {
            value: 'S9R',
            type: 'SENIOR',
          },
        ];
      } else if (clusterCode.toLowerCase() === 'corp') {
        if (REWARDS_REDEMPTION_CLUSTER_CODES.includes(corporateCode?.toUpperCase())) {
          setIsRedemption(true);
          rateRequestTypes = [
            {
              type: 'REDEMPTION',
            },
          ];
        } else {
          rateRequestTypes = [
            {
              value: corporateCode?.trim(),
              type: 'CLUSTER',
            },
          ];
        }
      } else if (clusterCode.toLowerCase() === GROUP_CLUSTER_CODE) {
        /*
        Logic to ignore the group codes on ADF page, remove this once group codes are enabled for ADF
      */
        rateRequestTypes = [
          {
            value: '',
            type: 'STANDARD',
          },
        ];
      }
    }
    return rateRequestTypes;
  };

  // callback function that will be used by the replace function in getFormattedErrorKey
  // used to remove full stops and convert to camel case
  function replacer(match: string): string {
    return match?.[1]?.toUpperCase();
  }

  const fetchMandatoryFees = (mandatoryFeePayload: any) => {
    client
      .query({
        query: phoenixShopHQVRateOnly,
        fetchPolicy: 'network-only',
        variables: mandatoryFeePayload?.variables,
        context: mandatoryFeePayload?.context,
      })
      .then((resp: any) => {
        const manFeesValue =
          resp?.data?.search?.lowestAvailableRates?.searchByPropertyIds?.edges?.[0]?.node?.rates?.[0]?.rateModes
            ?.lowestAverageRate?.mandatoryFees?.amount /
          Math.pow(
            10,
            resp?.data?.search?.lowestAvailableRates?.searchByPropertyIds?.edges?.[0]?.node?.rates?.[0]?.rateModes
              ?.lowestAverageRate?.mandatoryFees?.decimalPoint
          );
        setFromCurrency(
          resp?.data?.search?.lowestAvailableRates?.searchByPropertyIds?.edges?.[0]?.node?.rates?.[0]?.rateModes
            ?.lowestAverageRate?.mandatoryFees?.currency
        );
        ersManFees.current = manFeesValue ? manFeesValue : 0;
        setDataLoading(false);
      })
      .catch(() => {
        setDataLoading(false);
      });
  };

  // function that accepts dot separated string and returns camel cased key
  function getFormattedErrorKey(input: string) {
    return input.toLowerCase().replace(/\../g, replacer);
  }

  function returnApplicableAlert(matchKey: string) {
    const returnedAlert: AlertMessageBodyProps = {};
    const shortlistedAlert = alertBody?.filter(
      alert => alert?.adfAlertMsgTypeName.toLowerCase() === matchKey.toLowerCase()
    )?.[0];
    returnedAlert['alertMessage'] = shortlistedAlert?.adfAlertCodeListItems[0]?.['adfAlertMessage'];
    returnedAlert['alertHeading'] = shortlistedAlert?.adfAlertCodeListItems[0]?.['adfAlertHeading'];
    returnedAlert['alertCode'] = shortlistedAlert?.adfAlertCodeListItems[0]?.['adfAlertCode'];
    returnedAlert['alertType'] = shortlistedAlert?.adfAlertCodeListItems[0]?.['adfAlertType'];
    returnedAlert['alertMsgTypeName'] = shortlistedAlert?.['adfAlertMsgTypeName'];
    returnedAlert['alertLinkText'] = shortlistedAlert?.adfAlertCodeListItems[0]?.['adfAlertMessageText'] ?? null;
    returnedAlert['alertMessageLink'] =
      shortlistedAlert?.adfAlertCodeListItems[0]?.['adfAlertMessageRedirection'] ?? null;
    if (matchKey === FUTURE_DATE_ERRROR) {
      const date = inventoryDates ?? '';
      returnedAlert['alertMessage'] += ' ' + date.split('-').reverse().join('-');
    }
    window.scroll({
      top: 0,
      behavior: 'smooth',
    });
    return returnedAlert;
  }

  useEffect(() => {
    displayErrorMessageOnLoad();
  }, []);

  useEffect(() => {
    if (tabList?.length === 1) {
      setSelectedTab(tabList[0].tabValue === TOTAL_TAB ? TOTAL_TAB : AVG_TAB);
    }
  }, [tabList]);

  useEffect(() => {
    displaySessionBasedAlerts(updatedSessionData);
  }, [updatedSessionData]);

  const displayErrorMessageOnLoad = (skipCalls = false) => {
    const queries = new URLSearchParams(window.location.href);
    const roomCount = queries?.get('roomCount') || queries?.get('numberOfRooms') || '';
    const corpCode = corporateCode || queries?.get('corporateCode') || '';
    const clusCode = clusterCode || queries?.get('clusterCode') || '';
    const childCount = (childrenCount || AriesReservation?.availabilityRequestVO?.numChildrenPerRoom) ?? 0;
    validateAvailablitySearchFormData(
      flexibleDate,
      roomCount,
      corpCode,
      clusCode,
      '',
      false,
      childCount,
      isAdultsOnly,
      {},
      skipCalls
    );
  };

  useEffect(() => {
    displayErrorMessageOnLoad(true);
  }, [isAdultsOnly]);

  const validateAvailabilitySearchRequest = async (
    flexible: string | boolean,
    newSearchParams: string,
    obj: any,
    searchCTAEndpointURL?: string
  ) => {
    setIsInvalidCorpCode(false);
    const response = await validateAvailabilityRequest(
      flexible?.toString() === 'true',
      searchCTAEndpointURL ?? NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL,
      propertyId,
      currentCurrency,
      includeTaxesAndFees,
      interceptorResponse,
      preHeader,
      true,
      newSearchParams,
      false,
      headersData,
      isBulgariDomain
    );
    if (response) {
      const updatedConcurClusterCode = getClusterCodeFromRateRequestType(
        obj.search.options.rateRequestTypes,
        clusterCode
      );

      updateDataLayerOnInteractions(
        resolvedUrl,
        headersData,
        query,
        datalayerParams,
        currentLocale,
        sessionID,
        propertyId,
        search_fusionsearchid,
        isBulgariDomain,
        undefined,
        undefined,
        updatedConcurClusterCode
      );
      setSessionData(response?.data?.sessionData);
      setUpdatedSessionData(response?.data?.sessionData);
      updateGlobalData('sessionData', response?.data?.sessionData);
      displaySessionBasedAlerts(response?.data?.sessionData);
      const redirectSuccessUri = response?.headers?.['location'] || response?.headers?.['Location'];
      const isNotNavigatingToAdf =
        redirectSuccessUri && redirectSuccessUri?.indexOf('/search/availabilityCalendar') === -1;
      if (response.status === 200 && !isNotNavigatingToAdf) {
        //calling uxl only incase of staying in the page without redirection
        setDataLoading(true);
        if (!skipQuery) {
          getADFSearchDataForDTT(obj, response?.data?.sessionData);
        } else {
          // Author mode: use mock response
          setAdfData(mockResponse.data.search);
          setDataLoading(false);
        }
      }
    }
  };

  async function displayApplicableAlert(
    flexible: string | boolean,
    roomsCount: string,
    corporateCode: string,
    clusterCode: string,
    childCount: number | string,
    isAdultsOnly: boolean,
    sessionDataValue?: typeof sessionData
  ) {
    corporateCode = corporateCode?.trim();
    setAlertMessage({});
    let applicableAlert: AlertMessageBodyProps = {};
    let errorKey = '';

    // Check if both currency fallback keys exist in sessionData (presence check only)
    // This check has highest precedence
    const hasCurrencyFallbackKeys =
      sessionDataValue &&
      'fallback_exists_tip_call' in (sessionDataValue?.data?.AriesReservation || {}) &&
      'fallback_type_tip_call' in (sessionDataValue?.data?.AriesReservation || {});

    // Alternative check for cacheData structure
    const hasCurrencyFallbackKeysInCache =
      sessionDataValue &&
      'fallback_exists_tip_call' in (sessionDataValue?.cacheData?.data?.AriesReservation || {}) &&
      'fallback_type_tip_call' in (sessionDataValue?.cacheData?.data?.AriesReservation || {});

    if (hasCurrencyFallbackKeys || hasCurrencyFallbackKeysInCache) {
      // Currency conversion failed - highest priority error
      errorKey = CURRENCY_CONVERSION_FAILED;
    }
    // All other error checks go in the else block
    // error case when flexible dates are true and dates are beyond inventory dates
    // Error key: checkin.date.future.not.allowed.invalid
    else if (moment(checkInDate).diff(moment(inventoryDates), 'd') > 0) {
      errorKey = FUTURE_DATE_ERRROR;
    }
    // error case when flexible dates are true and rooms are greater than 3
    // Error key: flexible.date.room.count.invalid
    else if ((flexible === 'true' || flexibleDate === true) && parseInt(roomsCount, 10) > 3) {
      errorKey = TOO_MANY_ROOMS_FOR_FLEXIBLE_ERROR;
    }
    // error case when property is adults only and child out greater than zero
    else if (isAdultsOnly && Number(childCount) > 0) {
      errorKey = ADULT_ONLY_CHILD_COUNT_MORE_THAN_ZERO_ERROR;
    }
    // error case when group code has insufficient characters
    // Error key: group.code.fewer.than.7.char.error.text
    else if (
      corporateCode?.length < 7 &&
      corporateCode?.length >= 1 &&
      clusterCode.toLowerCase() === GROUP_CLUSTER_CODE
    ) {
      errorKey = GROUP_CODE_FEW_CHARS_ERROR;
      setIsInvalidCorpCode(true);
    } else if (
      corporateCode?.length >= 1 &&
      clusterCode?.toLowerCase() === CORP_CLUSTER_CODE &&
      LATIN_NAME_REGEX.test(corporateCode) !== true
    ) {
      errorKey = CORP_CODE_CHARS_INVALID_ERROR;
      setIsInvalidCorpCode(true);
    } else if (
      (corporateCode?.length > 9 || corporateCode?.length < 2) &&
      corporateCode?.length >= 1 &&
      clusterCode?.toLowerCase() === CORP_CLUSTER_CODE
    ) {
      errorKey = CORP_CODE_CHARS_LENGTH_ERROR;
      setIsInvalidCorpCode(true);
    }
    // if not the above two errors, there could be an error in the session doc
    else {
      errorKey = '';
      setIsInvalidCorpCode(false);
    }

    if (errorKey && errorKey !== undefined && errorKey !== '') {
      applicableAlert = returnApplicableAlert(errorKey);
      if (applicableAlert && Object.keys(applicableAlert).length > 0) {
        setAlertMessage(applicableAlert);
      }
    }
    return applicableAlert;
  }

  async function validateAvailablitySearchFormData(
    flexible: string | boolean,
    roomsCount: string,
    corporateCode: string,
    clusterCode: string,
    newSearchParams: string,
    fromUpdateSearch: boolean,
    childCount: number | string,
    isAdultsOnly: boolean,
    obj: any,
    skipCalls = false,
    searchCTAEndpointURL?: string
  ) {
    const queries = new URLSearchParams(window.location.href);
    const isAdultsOnlyProperty = queries?.has('isAdultsOnly')
      ? queries?.get('isAdultsOnly') === 'true'
        ? true
        : false
      : isAdultsOnly;
    const applicableAlert = await displayApplicableAlert(
      flexible,
      roomsCount,
      corporateCode,
      clusterCode,
      childCount,
      isAdultsOnlyProperty,
      updatedSessionData
    );
    if (
      !skipCalls &&
      !(
        applicableAlert &&
        Object.keys(applicableAlert).length > 0 &&
        applicableAlert?.alertMsgTypeName !== CURRENCY_CONVERSION_FAILED
      ) &&
      fromUpdateSearch
    ) {
      await validateAvailabilitySearchRequest(flexible, newSearchParams, obj, searchCTAEndpointURL);
    }
  }

  function displaySessionBasedAlerts(sessionDataValue: typeof sessionData) {
    const { AriesReservation } = (sessionDataValue?.data ?? sessionDataValue?.cacheData?.data) || {};
    let errorKey = '';
    let applicableAlert: AlertMessageBodyProps = {};
    if (AriesReservation?.errorMessages?.availabilitySearchFormErrorMessages) {
      const sessionErrorMessages =
        AriesReservation?.errorMessages?.availabilitySearchFormErrorMessages?.errorMessageKeys;
      const sessionErrorKey = sessionErrorMessages?.[0];
      const splitErrorMessages = ('|' + sessionErrorKey)?.split('|');
      errorKey = getFormattedErrorKey(splitErrorMessages[splitErrorMessages.length - 1] ?? '');
    }
    if (errorKey) {
      applicableAlert = returnApplicableAlert(errorKey);
      if (applicableAlert && applicableAlert.alertCode) {
        setAlertMessage(applicableAlert);
      }
    }
  }

  /**
   * Function to count the no of days from previous month displayed in current month view
   * @param {moment.MomentInput}  day selected month passed to fetch the days
   */

  const getUTCNoOfPrevMonthDays = (day: moment.MomentInput) => {
    let noOfPrevMonthDays =
      Number(moment(moment(day).utc().format(DATE_FORMAT_YEAR_MONTH_DAY)).day()) || NUMBER_OF_DAYS_FOR_PREVIOUS_MONTH;
    if (noOfPrevMonthDays === 6) {
      if (moment(day).startOf('M')?.format('dddd').toString()?.toLowerCase() === 'sunday') {
        noOfPrevMonthDays = Number(jsonContent['firstDayOfWeek']) === 1 ? 7 : 0;
      } else {
        noOfPrevMonthDays = 6;
      }
    }
    return Number(jsonContent['firstDayOfWeek']) > 0
      ? noOfPrevMonthDays - Number(jsonContent['firstDayOfWeek'])
      : noOfPrevMonthDays;
  };

  const updateAvailabilityCalendarData = (data: any) => {
    setFormData(data);
    const { formValues, params, flexible, searchCTAEndpointURL } = data;
    const { dates, usePoints, roomsAndGuests, specialRates } = formValues;
    const { fromDate, toDate } = dates;

    const { adultsCount, childrenCount, roomsCount } = roomsAndGuests;
    const { clusterCode, corporateCode: corpCode, groupCode: grpCode } = specialRates;
    const corporateCode = corpCode ? corpCode?.trim() : '';
    const groupCodeValue = grpCode ? grpCode?.trim() : '';
    const rateCode = clusterCode?.toLowerCase() === 'group' ? groupCodeValue : corporateCode;
    const rateRequestTypes = getConcurAwareRateRequestType(
      usePoints.value,
      clusterCode,
      corporateCode,
      groupCodeValue,
      userDetailsState,
      getRateRequestType
    );
    const searchParams = new URLSearchParams(params);
    const queries = new URLSearchParams(window.location.href);
    searchParams.delete('isRateCalendar');
    searchParams.delete('isSearch');
    searchParams.delete('currency');
    // Handle special case for group cluster code
    if (searchParams.get('clusterCode')?.toLocaleLowerCase() === 'group') {
      searchParams.set('clusterCode', 'none');
      searchParams.delete('groupCode');
    }
    // Remove the incorrect parameter numberOfAdults from /validate/availabilitySearch request parameters
    if (!searchParams.has('numAdultsPerRoom')) {
      searchParams.set('numAdultsPerRoom', searchParams.get('numberOfAdults') || '');
    }
    searchParams.delete('numberOfAdults');
    if (queries?.has('isAdultsOnly')) {
      searchParams.set('isAdultsOnly', queries.get('isAdultsOnly') || isAdultsOnly ? 'true' : 'false');
    }

    let newSearchParams = searchParams.toString();
    //to check if we are getting data from searchProducts and comparing with Dates
    //We will only get those dates from searchProducts for which dates are available for checkin
    if (
      adfData?.calendarSearchByProperty?.edges?.findIndex((edge: any) => {
        const startDate = edge.node.startDate;
        return startDate === fromDate?.format(DATE_FORMAT_YEAR_MONTH_DAY);
      }) === -1
    ) {
      newSearchParams = searchParams.toString() + 'unsuccessfulSell=true';
      window.history.replaceState({}, headersData?.referer, decodeURIComponent(`?${newSearchParams}`));
    }

    setSelectedCluster(clusterCode);
    setSelectedCorporateCode(rateCode);
    setSelectedNumOfGuests(adultsCount + childrenCount);
    setChildrenCount(childrenCount);

    setSelectedRoomCount(roomsCount);
    if (toDate && toDate?.diff) {
      setSelectedDays(toDate?.diff(fromDate, 'days'));
    }

    if (toDate && toDate?.diff && toDate?.diff(fromDate, 'days') === 1) {
      setSelectedTab(TOTAL_TAB);
    } else {
      setSelectedTab(AVG_TAB);
    }
    setIsPointsSelected(usePoints?.value);
    const lastMonthDates = moment(fromDate)?.startOf('M').subtract(getNoOfPrevMonthDays(fromDate), 'days'); // get date 6 days older to current month date
    const isPastDate = lastMonthDates?.diff(moment()) < 0 ? true : false; // check if above date is older than today
    const startDate = isPastDate ? moment() : lastMonthDates; // if 'lastMonthDates' is past date then fromDate is today , else fromDate is 'lastMonthDates'
    const endDate = startDate
      ?.clone()
      .add(
        isPastDate && lastMonthDates.format('MMMM') === moment().format('MMMM')
          ? Number(getNoOfPrevMonthDays(fromDate) >= 5 ? TOTAL_NUMBER_OF_DAYS_FOR_DATA : 34) +
              Number(lastMonthDates?.diff(moment(), 'days'))
          : getNoOfPrevMonthDays(fromDate) >= 5
          ? TOTAL_NUMBER_OF_DAYS_FOR_DATA
          : 34,
        'days'
      )
      .format(DATE_FORMAT_YEAR_MONTH_DAY);
    const startDateFormatted = startDate?.format(DATE_FORMAT_YEAR_MONTH_DAY);
    if (fromDate && fromDate?.format && fromDate?.format(DATE_FORMAT_YEAR_MONTH_DAY)) {
      setdefaultMonthDate(checkLocaleAndConvertNumberInDate(fromDate?.format(DATE_FORMAT_YEAR_MONTH_DAY)));
    }
    // change default month to show on ADF calendar based on start date

    const obj = {
      id: [propertyId],
      search: {
        propertyId: propertyId,
        options: {
          startDate: checkLocaleAndConvertNumberInDate(startDateFormatted),
          numberOfRooms: Number(roomsCount),
          rateRequestTypes: rateRequestTypes,
          endDate:
            moment(endDate).diff(moment(inventoryDates), 'd') > 0
              ? checkLocaleAndConvertNumberInDate(
                  moment(inventoryDates).subtract(1, 'd').format(DATE_FORMAT_YEAR_MONTH_DAY)
                )
              : checkLocaleAndConvertNumberInDate(endDate),
          numberInParty: adultsCount + childrenCount,
          numberOfDays:
            toDate && toDate?.diff
              ? Math.trunc(toDate?.diff(fromDate, 'days')) > 9
                ? 1
                : Math.trunc(toDate?.diff(fromDate, 'days'))
              : Math.trunc(lengthOfStay), //In case of flexible search maximum allowed nights is 9 so  length of stay is reset to 1 incase of morethan 9 nights in flexible and  need this variable to calculate total price
          customerId: consumerID,
        },
      },
    };

    validateAvailablitySearchFormData(
      flexible,
      roomsCount,
      corporateCode,
      clusterCode,
      newSearchParams,
      true,
      childrenCount,
      isAdultsOnly,
      obj,
      false,
      searchCTAEndpointURL
    );
    setDateRange([checkLocaleAndConvertNumberInDate(startDateFormatted), checkLocaleAndConvertNumberInDate(endDate)]);

    /**
     * To get and set Counter value in sessionStorage
     * and later updating hash in the url on update search
     * */
    SearchCounterValue();
  };

  //below function calls UXL for ADF data/rates when user changes search form data from ADF page
  useEffect(() => {
    /**
     * Event to capture the user values from search form
     */
    eventUtil.on('availabilityCalendarSearchFormData', data => {
      updateAvailabilityCalendarData(data);
    });
    return (): void => {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      eventUtil.remove('availabilityCalendarSearchFormData', () => {});
    };
  }, []);

  // Replaced event with store-driven flow:
  const destination = useSearchFormStore((state: any) => state?.destination);
  const destinationHiddenFields = useSearchFormStore((state: any) => state?.destinationHiddenFields);
  const roomsAndGuests = useSearchFormStore((state: any) => state?.roomsAndGuests);
  const dates = useSearchFormStore((state: any) => state?.dates);
  const usePoints = useSearchFormStore((state: any) => state?.usePoints);
  const specialRates = useSearchFormStore((state: any) => state?.specialRates);
  const destinationFieldDetails = useSearchFormStore((state: any) => state?.destinationFieldDetails);
  const searchCTAClicked = useSearchFormStore((state: any) => state?.searchCTAClicked);
  const setSearchCTAClicked = useSearchFormStore((state: any) => state?.setSearchCTAClicked);

  const formValues = convertStateValuestoFormValues(
    destination,
    roomsAndGuests,
    dates,
    usePoints,
    specialRates,
    destinationHiddenFields
  );
  const flexible = dates?.flexible;
  const params = formAvailablitySearchRequestParams(
    destination,
    destinationHiddenFields,
    roomsAndGuests,
    dates,
    usePoints,
    specialRates,
    destinationFieldDetails,
    currentLocale
  );

  useEffect(() => {
    if (!searchCTAClicked) return;
    updateAvailabilityCalendarData({ formValues, params, flexible, searchCTAEndpointURL });
    setSearchCTAClicked?.(false);
  }, [searchCTAClicked]);

  /**
   * Function to count the no of days from previous month displayed in current month view
   * @param {moment.MomentInput}  day selected month passed to fetch the days
   */

  const getNoOfPrevMonthDays = (day: moment.MomentInput) => {
    let noOfPrevMonthDays = Number(moment(day).startOf('M').day()) || NUMBER_OF_DAYS_FOR_PREVIOUS_MONTH;

    if (noOfPrevMonthDays === 6) {
      if (moment(day).startOf('M')?.format('dddd').toString()?.toLowerCase() === 'sunday') {
        noOfPrevMonthDays = Number(jsonContent['firstDayOfWeek']) === 1 ? 7 : 0;
      } else {
        noOfPrevMonthDays = 6;
      }
    }
    return Number(jsonContent['firstDayOfWeek']) > 0
      ? noOfPrevMonthDays - Number(jsonContent['firstDayOfWeek'])
      : noOfPrevMonthDays;
  };
  //function gets called when user changes months manually
  const getRatesOnMonthChange = (month: Date) => {
    const lastMonthDates = moment(month)?.startOf('M').subtract(getNoOfPrevMonthDays(month), 'days'); // get date 6 days older to current month date
    const isPastDate = lastMonthDates?.diff(moment()) < 0 ? true : false; // check if above date is older than today
    const fromDate = isPastDate ? moment() : lastMonthDates; // if 'lastMonthDates' is past date then fromDate is today , else fromDate is 'lastMonthDates'
    const endDate = fromDate
      ?.clone()
      .add(
        isPastDate && lastMonthDates.format('MMMM') === moment().format('MMMM')
          ? Number(getNoOfPrevMonthDays(month) >= 5 ? TOTAL_NUMBER_OF_DAYS_FOR_DATA : 34) +
              Number(lastMonthDates?.diff(moment(), 'days'))
          : getNoOfPrevMonthDays(month) >= 5
          ? TOTAL_NUMBER_OF_DAYS_FOR_DATA
          : 34,
        'days'
      )
      .format(DATE_FORMAT_YEAR_MONTH_DAY);
    const formDataFromatted = checkLocaleAndConvertNumberInDate(fromDate.format(DATE_FORMAT_YEAR_MONTH_DAY));
    setdefaultMonthDate(checkLocaleAndConvertNumberInDate(moment(month).format(DATE_FORMAT_YEAR_MONTH_DAY)));
    const obj = {
      id: [propertyId],
      search: {
        propertyId: propertyId,
        options: {
          startDate: checkLocaleAndConvertNumberInDate(formDataFromatted),
          numberOfRooms: Number(selectedRoomCount),
          rateRequestTypes: getConcurAwareRateRequestType(
            isPointsSelected,
            selectedCluster,
            selectedCorporateCode,
            selectedCorporateCode,
            userDetailsState,
            getRateRequestType
          ),
          endDate:
            moment(endDate).diff(moment(inventoryDates), 'd') > 0
              ? checkLocaleAndConvertNumberInDate(
                  moment(inventoryDates).subtract(1, 'd').format(DATE_FORMAT_YEAR_MONTH_DAY)
                )
              : checkLocaleAndConvertNumberInDate(endDate),
          numberInParty: selectedNumOfGuests,
          numberOfDays: Math.trunc(selectedDays) > 9 ? 1 : Math.trunc(selectedDays), //In case of flexible search maximum allowed nights is 9  so length of stay is reset to 1 incase of morethan 9 nights in flexible and  need this variable to calculate total price
          customerId: consumerID,
        },
      },
    };

    setDateRange([checkLocaleAndConvertNumberInDate(formDataFromatted), checkLocaleAndConvertNumberInDate(endDate)]);

    setDataLoading(true);

    if (!skipQuery) {
      getADFSearchDataForDTT(obj);
    }
  };
  useMemo(() => {
    replaceDays();
  }, [selectedDays, selectedTab]);
  /**
   * Function to render the content to be display in mini property card
   * @param {number}  item  which contains the selected tab data
   */
  const handleTabs = (item: string) => {
    setSelectedTab(item);
  };

  /**
   * Datalayer call on interactions and component render
   */
  useEffect(() => {
    const loadDatalayer = async (): Promise<void> => {
      updateDataLayerObj(window?.dataLayer ?? {}, datalayerProperties);
    };
    loadDatalayer();
    // Get the current URL
    const currentUrl = window.location.href;
    // Parse the URL to extract the query parameters
    const url = new URL(currentUrl);
    const params = new URLSearchParams(url.search);
    const currentCostTab = selectedTab;
    // Update the costTab parameter in the URL
    if (currentCostTab === AVG_TAB) {
      params.set('costTab', 'average');
    } else {
      params.set('costTab', 'total');
    }
    // Reconstruct the URL with the updated query parameters
    url.search = params.toString();
    // Replace the current URL with the updated URL
    window.history.replaceState({}, '', url.toString());
  }, [formData, selectedTab]);

  const calendarComp = () => {
    return (
      <CalendarWrapper
        selectedTab={selectedTab}
        adfData={adfData}
        isDataLoading={dataLoading}
        formData={formData ? formData.formValues : {}}
        jsonContent={jsonContent}
        onMonthChange={getRatesOnMonthChange}
        defaultMonthDate={defaultMonthDate}
        inventoryData={inventoryDates}
        isPointsSelected={isPointsSelected}
        currentCurrency={currentCurrency}
        numberOfDays={selectedDays}
        dateRange={dateRange}
        propertyId={propertyId}
        interceptorResponse={interceptorResponse}
        preHeader={preHeader}
        selectedCurrency={selectedCurrency}
        setAlertMessage={setAlertMessage}
        returnApplicableAlert={returnApplicableAlert}
        isRedemption={isRedemption}
        fromCurrency={fromCurrency}
        ersManFees={ersManFees.current ?? 0}
        isErsProperty={AriesReservation?.ersProperty}
        isAlertMessage={
          !alertMessage?.alertMessage || alertMessage?.alertMsgTypeName === CURRENCY_CONVERSION_FAILED ? false : true
        }
        skipQuery={skipQuery}
      />
    );
  };

  return showCalendar ? (
    <StyledAvailabilityCalendarDiv
      data-component-name="o-shop-availabilitycalendar"
      data-testid="availabilitycalendar"
      className={clsx('px-0 standard', 'py-md-4', 'm-container-fullbleed', isDesktop ? 'py-5' : 'mb-5')}
    >
      {alertMessage?.alertMessage && (
        <Messages
          className="adf-error-messages"
          messageType={alertMessage?.alertType}
          messageHeading={alertMessage?.alertHeading}
          messageText={alertMessage?.alertMessage}
          linkText={alertMessage?.alertLinkText ?? undefined}
          messageLink={alertMessage?.alertMessageLink ?? undefined}
          AlertLinkTextLabel={alertMessage?.alertLinkText ?? undefined}
          MessageRedirection={alertMessage?.alertMessageLink ?? undefined}
          messageContentTabIndex={0}
          messageRole="alert"
        />
      )}
      {(isPointsSelected || isRedemption) && (
        <Text
          copyText={model?.['pointsLegalText']}
          customClass="points-legal-helper-text container"
          fontSize={isViewportM ? size.small : size.extraSmall}
          element={tags.div}
        />
      )}
      <TabComponent
        tabList={tabList}
        setSelectedTab={handleTabs}
        selectedTab={selectedTab}
        clickTrackingLoc="ADFCalendar"
        hideTabs={isPointsSelected || isRedemption}
        hidetabsAndContent={false}
        children={calendarComp()}
        classTabsEnabled={isPointsSelected && 'points-enabled'}
        customClass={'availability-tab-wrapper'}
        ariaRole="button"
      />
      {!isPointsSelected && !isRedemption && (
        <Text
          copyText={selectedTab === TOTAL_TAB ? model?.['avgTotalCostLegalText'] : model?.['avgNightlyCostLegalText']}
          customClass="legal-helper-text container"
          fontSize={size.extraSmall}
          element={tags.div}
        />
      )}
    </StyledAvailabilityCalendarDiv>
  ) : (
    <StyledAvailabilityCalendarDiv
      data-component-name="o-shop-availabilitycalendar"
      data-testid="availabilitycalendar-skeleton"
      className={clsx('px-0 standard', 'py-md-4', 'm-container-fullbleed', isDesktop ? 'py-5' : 'mb-5')}
    >
      <div className="container calendar-skeleton-loader">
        <SkeletonLoader />
      </div>
    </StyledAvailabilityCalendarDiv>
  );
};

export const AvailibilityCalendarConfig = {
  emptyLabel: 'AvailabilityCalendarComponent',
  isEmpty: false,
  resourceType: 'mi-aem-shop-spa/components/content/availabilitycalendar/v1/availabilitycalendar',
};

export const AvailabilityCalendarEditable = (props: any) => (
  <EditableComponent config={AvailibilityCalendarConfig} {...props}>
    <AvailabilityCalendar {...props} />
  </EditableComponent>
);
